import { useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Box, Typography, Card, CardContent, Stack, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, IconButton, CircularProgress,
} from '@mui/material';
import SaveIcon  from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import authFetch from '../utils/authFetch';
import { card, btn, inputSx } from '../styles/ds';
import { CURRENCY_CHOICES, PAY_METHOD_CHOICES, PAY_STATUS_CHOICES } from './paymentChoices';

const EMPTY_PAYMENT = { amount: '', currency: 'ILS', payment_method: 'bank_transfer', status: 'paid' };

// Inline "add payment" form used inside the order edit screen.
const AddPaymentForm = ({ orderId, onSaved, onClose }) => {
  const notify = useNotify();
  const [fields, setFields] = useState(EMPTY_PAYMENT);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!fields.amount || Number(fields.amount) <= 0) {
      notify('נא להזין סכום', { type: 'warning' }); return;
    }
    setSaving(true);
    try {
      const res = await authFetch('/payments', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ ...fields, order_id: orderId, amount: Number(fields.amount) }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'שגיאה בשמירת התשלום');
      }
      notify('התשלום נוסף בהצלחה', { type: 'success' });
      setFields(EMPTY_PAYMENT);
      onSaved();
    } catch (e) {
      notify(e.message || 'שגיאה בשמירת התשלום', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ ...card.highlight, mb: 2 }}>
      <CardContent className="crm-card-content" sx={{ p: 2.5 }}>
        <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography fontWeight={700} fontSize={14} color="primary.main">הוספת תשלום</Typography>
          <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <TextField
            label="סכום" type="number" size="small" value={fields.amount}
            onChange={e => set('amount', e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            sx={inputSx} fullWidth
          />
          <FormControl fullWidth size="small">
            <InputLabel>מטבע</InputLabel>
            <Select value={fields.currency} onChange={e => set('currency', e.target.value)} label="מטבע" sx={inputSx}>
              {CURRENCY_CHOICES.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>אמצעי תשלום</InputLabel>
            <Select value={fields.payment_method} onChange={e => set('payment_method', e.target.value)} label="אמצעי תשלום" sx={inputSx}>
              {PAY_METHOD_CHOICES.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>סטטוס</InputLabel>
            <Select value={fields.status} onChange={e => set('status', e.target.value)} label="סטטוס" sx={inputSx}>
              {PAY_STATUS_CHOICES.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Stack className="crm-actions" direction="row" spacing={1.5} justifyContent="flex-end">
          <Button variant="outlined" size="small" onClick={onClose}
            sx={{ borderColor: '#ccc', color: '#666', borderRadius: 2 }}>ביטול</Button>
          <Button variant="contained" size="small" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            sx={btn.primary}>
            שמור תשלום
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AddPaymentForm;
